// src/context/AuthContext.jsx
import { createContext, useContext, useEffect, useState } from "react";
import { AUTH_LINK_TYPE_FLAG, supabase } from "../lib/supabase";

const AuthContext = createContext(null);
const PASSWORD_SETUP_FLAG = "meten-password-setup-user";

function getAuthActionType() {
  if (typeof window === "undefined") return null;

  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const queryParams = new URLSearchParams(window.location.search);
  return (
    hashParams.get("type") ||
    queryParams.get("type") ||
    window.sessionStorage.getItem(AUTH_LINK_TYPE_FLAG)
  );
}

function readPendingPasswordSetupUser() {
  if (typeof window === "undefined") return null;
  return window.sessionStorage.getItem(PASSWORD_SETUP_FLAG);
}

function writePendingPasswordSetupUser(userId) {
  if (typeof window === "undefined") return;
  if (userId) {
    window.sessionStorage.setItem(PASSWORD_SETUP_FLAG, userId);
    return;
  }
  window.sessionStorage.removeItem(PASSWORD_SETUP_FLAG);
}

function clearAuthActionType() {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(AUTH_LINK_TYPE_FLAG);
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [isSessionReady, setIsSessionReady] = useState(false);
  const [mustSetPassword, setMustSetPassword] = useState(false);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (!isMounted) return;

        if (error) {
          console.error("Supabase getSession error:", error);
        }

        const s = data?.session ?? null;
        const actionType = getAuthActionType();
        if (s?.user?.id && (actionType === "invite" || actionType === "recovery")) {
          writePendingPasswordSetupUser(s.user.id);
          clearAuthActionType();
        }
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.user?.id && readPendingPasswordSetupUser() === s.user.id) {
          setMustSetPassword(true);
        } else {
          setMustSetPassword(false);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error("Unexpected getSession error:", err);
      } finally {
        if (!isMounted) return;
        setIsSessionReady(true);
      }
    })();

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const nextUser = session?.user ?? null;
        const actionType = getAuthActionType();
        const shouldRequirePasswordSetup =
          Boolean(nextUser?.id) && (actionType === "invite" || actionType === "recovery");

        if (shouldRequirePasswordSetup) {
          writePendingPasswordSetupUser(nextUser.id);
          clearAuthActionType();
        }

        if (!nextUser) {
          writePendingPasswordSetupUser(null);
          clearAuthActionType();
        }

        setSession(session);
        setUser(nextUser);
        setMustSetPassword(
          Boolean(nextUser?.id) && readPendingPasswordSetupUser() === nextUser.id
        );
        setIsSessionReady(true);
      }
    );

    return () => {
      isMounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const login = async ({ email, password }) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    setSession(data.session);
    setUser(data.session?.user ?? null);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
  };

  const changePassword = async ({ password }) => {
    const { data, error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
    return data;
  };

  const clearPasswordSetupRequirement = () => {
    writePendingPasswordSetupUser(null);
    setMustSetPassword(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isSessionReady,
        login,
        logout,
        changePassword,
        clearPasswordSetupRequirement,
        mustSetPassword,
        accessToken: session?.access_token || null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
